import _ from "lodash";
import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  jsonKeyMapping,
  NO_GROUP,
  templateActionEnum,
} from "../Config/Constants";
import TemplateDataContext from "../Contexts/TemplateData.context";
import { TemplateObject } from "../Models/TemplateObject";

type useLoadStepsType = {
  templates: TemplateObject;
};
export default async function useLoadSteps({
  templates,
}: useLoadStepsType): Promise<void> {
  const { stepName, template } = useParams();
  const { state, dispatch } = useContext(TemplateDataContext);
  const { activeTemplateSteps, isStepsOverride } = state;

  useEffect(() => {
    if (Object.keys(templates).length > 0) {
      /* ignore :  if any conditional step is added */
      if (isStepsOverride) return;
      // Ensure we have a valid template key
      const templateKey =
        template && templates[template] ? template : Object.keys(templates)[0];
      const selectTemplate = templates[templateKey];
      if (!selectTemplate || !Array.isArray(selectTemplate?.steps)) return;

      /* Conditional Steps */
      const conditionalSteps = _.groupBy(
        selectTemplate.steps.filter(
          (step: TemplateObject) => step[jsonKeyMapping.PARENT] !== undefined
        ),
        jsonKeyMapping.PARENT
      );
      dispatch({
        type: templateActionEnum.SET_CONDITIONAL_STEPS,
        payload: conditionalSteps,
      });
      /* filter non conditional steps */
      const stepList = selectTemplate.steps.filter(
        (step: TemplateObject) => step[jsonKeyMapping.PARENT] === undefined
      );
      const groupByStepsObject = _.groupBy(stepList, (x) =>
        _.capitalize(x[jsonKeyMapping.GROUP] || NO_GROUP)
      );

      const newGroupListSteps: TemplateObject[] = [];
      Object.keys(groupByStepsObject).forEach((group) => {
        if (group.toLowerCase() === NO_GROUP.toLowerCase()) {
          newGroupListSteps.push(...groupByStepsObject[group]);
          return;
        }
        newGroupListSteps.push({
          groupName: group,
          [group]: groupByStepsObject[group],
        });
      });
      // Prepare flat steps array (used for navigation) early so we can modify it (e.g., append a Review step)
      const steps = _.flatten(Object.values(groupByStepsObject));

      // Ensure a final "Review & Submit" step exists so users can review before completing
      // We'll append a lightweight informational step only if no step with slugify 'review-submit' exists
      const REVIEW_SLUG = "review-submit";
      const hasReviewStep = _.flatten(Object.values(groupByStepsObject)).some(
        (s: any) => s[jsonKeyMapping.SLUGIFY] === REVIEW_SLUG
      );
      if (!hasReviewStep) {
        const reviewStep: any = {
          [jsonKeyMapping.SLUG]: "Review Document",
          [jsonKeyMapping.SLUGIFY]: REVIEW_SLUG,
          // Intentionally omit DESCRIPTION so ReviewSummary sets tooltip & dynamic help via body.dataset
          [jsonKeyMapping.HAS_PREVIOUS]: true,
          [jsonKeyMapping.HAS_SUBMIT]: true,
        };
        // Append to NoGroup (ungrouped) end
        newGroupListSteps.push(reviewStep);
        // Also append to flat steps array so routing/navigation includes this review step
        steps.push(reviewStep);
      }
      dispatch({
        type: templateActionEnum.SET_ACTIVE_TEMPLATE_GROUP_BY,
        payload: newGroupListSteps,
      });
      /* update the steps order */
      dispatch({
        type: templateActionEnum.SET_ACTIVE_TEMPLATE_STEPS,
        payload: steps,
      });
    }

    return () => {};
  }, [
    stepName,
    template,
    activeTemplateSteps.length,
    Object.keys(templates).length,
  ]);
}
